// View "Lista" — tabela Antd com seleção em massa, status dropdown inline,
// origem clicável (deep-link pro módulo de origem) e click em row pra abrir.
import { Tag, Table, Select, Typography, Space, Empty, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import DataVencimento from './DataVencimento';
import { COR_MODULO, STATUS_LABEL, PRIORIDADE_LABEL } from './types';
import type { Atividade, AtividadesProps } from './types';

const { Text } = Typography;

interface Props {
  atividades: Atividade[];
  /** Na aba do Comercial, a coluna de origem vira "Oportunidade · Cliente". */
  moduloFixo?: AtividadesProps['moduloFixo'];
  loading: boolean;
  meuEmail: string;
  onEditar: (a: Atividade) => void;
  onAlterarStatus: (id: string, status: string) => void;
  onIrParaOrigem?: (modulo: string, origemTipo: string, origemId: string, refId: string | null) => void;
  selecionadas: string[];
  setSelecionadas: (ids: string[]) => void;
}

export default function ListaAtividades({
  atividades, loading, meuEmail, onEditar, onAlterarStatus, onIrParaOrigem,
  selecionadas, setSelecionadas, moduloFixo,
}: Props) {
  const irParaOrigem = (a: Atividade) => {
    if (onIrParaOrigem && a.origem_modulo && a.origem_id) {
      onIrParaOrigem(a.origem_modulo, a.origem_tipo || '', a.origem_id, a.referencia_id);
    }
  };
  const cols: ColumnsType<Atividade> = [
    {
      title: 'Status', dataIndex: 'status', key: 'st', width: 130,
      // Wrapper que para a propagação ANTES de chegar no onRow → onEditar.
      // O Antd Select renderiza o dropdown num portal, mas o click numa opção
      // bubbla pela árvore React (atravessa o portal). stopPropagation em
      // onClick + onMouseDown cobre os dois caminhos. Também: chamamos
      // alterarStatus dentro de um setTimeout pra garantir que o dropdown
      // feche ANTES de qualquer reflow disparar outro evento.
      render: (s, a) => (
        <div onClick={e => e.stopPropagation()}
             onMouseDown={e => e.stopPropagation()}>
          <Select
            value={s} size="small" style={{ width: 120 }}
            onChange={(v) => onAlterarStatus(a.id, v)}
            options={Object.entries(STATUS_LABEL).map(([k, v]) => ({ label: v.label, value: k }))}
          />
        </div>
      ),
    },
    {
      title: 'Atividade', dataIndex: 'titulo', key: 'tit',
      render: (_, a) => (
        <div>
          <Text strong>{a.titulo}</Text>
          {a.descricao && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }} ellipsis>{a.descricao}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Atribuído', dataIndex: 'atribuicoes', key: 'at', width: 200,
      render: (atribs: any[]) => atribs.length === 0
        ? <Text type="secondary" italic>—</Text>
        : (
          <Space direction="vertical" size={0}>
            {atribs.map((at: any) => (
              <Text key={at.email} style={{
                fontSize: 11, color: at.email.toLowerCase() === meuEmail.toLowerCase() ? '#0930D0' : '#666',
              }}>
                {at.email.toLowerCase() === meuEmail.toLowerCase() ? '★ ' : ''}{at.email}
              </Text>
            ))}
          </Space>
        ),
    },
    {
      // A OPORTUNIDADE e o CLIENTE da atividade do comercial (09/10/2026),
      // lidos do cadastro pela lista: o negócio (clicável) e o cliente embaixo.
      // Licitação: o edital e o órgão. Fora do comercial, a origem de sempre.
      title: moduloFixo === 'comercial' ? 'Oportunidade · Cliente' : 'Origem', key: 'or', width: 260,
      render: (_, a) => a.origem_modulo === 'comercial' && (a.negocio_titulo || a.cliente_nome) ? (
        <div style={{ maxWidth: 260, lineHeight: 1.3 }}>
          {a.negocio_titulo && (
            <Tooltip title={a.negocio_id ? 'Abrir o negócio' : (a.origem_label || a.negocio_titulo)}>
              <div onClick={(e) => { e.stopPropagation(); if (a.negocio_id) irParaOrigem(a); }}
                style={{ color: a.negocio_id ? '#0930D0' : undefined, cursor: a.negocio_id ? 'pointer' : 'default',
                         fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {a.origem_tipo === 'licitacao' ? 'Licitação ' : ''}{a.negocio_titulo}
              </div>
            </Tooltip>
          )}
          {a.cliente_nome && (
            <div style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={a.cliente_nome}>
              {a.cliente_nome}
            </div>
          )}
        </div>
      ) : a.origem_modulo ? (
        <Tag color={COR_MODULO[a.origem_modulo]?.tagColor || 'default'}
          style={{ cursor: onIrParaOrigem ? 'pointer' : 'default', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
          onClick={(e) => { e.stopPropagation(); irParaOrigem(a); }}
          title={`Abrir em ${COR_MODULO[a.origem_modulo]?.label || a.origem_modulo}`}>
          {COR_MODULO[a.origem_modulo]?.emoji || '📌'} {a.origem_label || `${a.origem_modulo}/${a.origem_id}`}
        </Tag>
      ) : <Tag color="default">👤 Pessoal</Tag>,
    },
    {
      title: 'Prazo', dataIndex: 'data_vencimento', key: 'pr', width: 130,
      render: (v, r) => v ? <DataVencimento iso={v} status={r.status} /> : <Text type="secondary" italic>—</Text>,
    },
    {
      title: 'Prioridade', dataIndex: 'prioridade', key: 'p', width: 100,
      render: (p) => {
        const info = PRIORIDADE_LABEL[p];
        return <Tag style={{ color: info?.cor, borderColor: info?.cor }}>{info?.label || p}</Tag>;
      },
    },
  ];

  return (
    <Table
      rowKey="id" columns={cols} dataSource={atividades} loading={loading}
      size="small" pagination={{ pageSize: 50, showSizeChanger: true }}
      rowSelection={{
        selectedRowKeys: selecionadas,
        onChange: (keys) => setSelecionadas(keys as string[]),
      }}
      onRow={(record) => ({
        onClick: () => {
          // Posts de marketing (vindos do UNION em listarAtividades) não abrem
          // o ModalAtividade — abrem o ModalPost no módulo Marketing via deep-link.
          if (record.origem_modulo === 'marketing' && record.origem_id && onIrParaOrigem) {
            onIrParaOrigem(record.origem_modulo, record.origem_tipo || '', record.origem_id, record.referencia_id);
          } else {
            onEditar(record);
          }
        },
        style: { cursor: 'pointer' },
      })}
      locale={{ emptyText: <Empty description={'Nenhuma atividade. Clique em "+ Nova atividade" para criar a primeira.'} /> }}
      scroll={{ x: 'max-content' }}
    />
  );
}
